<template>
  <div
    ref="wrapEl"
    class="playback-range-bar"
    v-loading="loading"
    element-loading-text="应用选区中"
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(255, 255, 255, 0.72)"
  >
    <div class="playback-range-bar__inner">
      <div v-if="isEmpty" class="playback-range-bar__empty">
        <div class="playback-range-bar__empty-line"></div>
      </div>
      <div v-show="!isEmpty" ref="chartEl" class="playback-range-bar__chart"></div>
      <template v-if="timeLabelDisplay.mode === 'split'">
        <span
          class="playback-range-bar__time playback-range-bar__time--start"
          :class="{ 'is-placeholder': isEmpty }"
          :style="{ left: timeLabelDisplay.startPct + '%' }"
        >{{ displayTimeStart }}</span>
        <span
          class="playback-range-bar__time playback-range-bar__time--end"
          :class="{ 'is-placeholder': isEmpty }"
          :style="{ left: timeLabelDisplay.endPct + '%' }"
        >{{ displayTimeEnd }}</span>
      </template>
      <span
        v-else
        class="playback-range-bar__time playback-range-bar__time--combined"
        :style="{ left: timeLabelDisplay.leftPct + '%' }"
      >{{ timeLabelDisplay.text }}</span>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getLayoutPrimaryColor, getLayoutPrimaryRgba } from '@/utils/layoutTheme'

const TIME_PLACEHOLDER = '0000-00-00 00:00:00'

function buildDataZoomStyle() {
  const primary = getLayoutPrimaryColor()
  return {
    bottom: 2,
    height: 22,
    borderColor: 'transparent',
    backgroundColor: '#f5f7fa',
    fillerColor: getLayoutPrimaryRgba(0.22),
    handleSize: '80%',
    handleStyle: { color: '#fff', borderColor: primary, borderWidth: 1 },
    dataBackground: {
      lineStyle: { color: primary, width: 1, opacity: 0.4 },
      areaStyle: { color: getLayoutPrimaryRgba(0.1) }
    },
    selectedDataBackground: {
      lineStyle: { color: primary, width: 1.2, opacity: 0.85 },
      areaStyle: { color: getLayoutPrimaryRgba(0.28) }
    },
    textStyle: { color: '#909399', fontSize: 10 },
    brushSelect: false,
    showDetail: false
  }
}

export default {
  name: 'PlaybackRangeBar',
  props: {
    points: {
      type: Array,
      default: () => []
    },
    rangeStart: {
      type: Number,
      default: 0
    },
    rangeEnd: {
      type: Number,
      default: 100
    },
    /** 0–1，全轨迹上的当前播放位置，用于在缩略条上显示指示线 */
    progressRatio: {
      type: Number,
      default: 0
    },
    /** 拖动后防抖等待/应用选区中 */
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chart: null,
      barInnerWidth: 0
    }
  },
  computed: {
    isEmpty() {
      const points = Array.isArray(this.points) ? this.points : []
      return points.length === 0
    },
    rangeTimeLabels() {
      const points = Array.isArray(this.points) ? this.points : []
      if (points.length < 2) return { start: '', end: '' }
      const toIdx = (pct) => {
        const p = Math.min(100, Math.max(0, Number(pct) || 0))
        return Math.min(points.length - 1, Math.max(0, Math.round((p / 100) * (points.length - 1))))
      }
      const startPt = points[toIdx(this.rangeStart)]
      const endPt = points[toIdx(this.rangeEnd)]
      return {
        start: startPt && startPt.time ? String(startPt.time) : '',
        end: endPt && endPt.time ? String(endPt.time) : ''
      }
    },
    displayTimeStart() {
      if (this.isEmpty) return TIME_PLACEHOLDER
      return this.rangeTimeLabels.start || TIME_PLACEHOLDER
    },
    displayTimeEnd() {
      if (this.isEmpty) return TIME_PLACEHOLDER
      return this.rangeTimeLabels.end || TIME_PLACEHOLDER
    },
    /** 选区过窄时合并为一行，避免起止时间叠字 */
    timeLabelDisplay() {
      const startPct = Math.min(100, Math.max(0, Number(this.rangeStart) || 0))
      const endPct = Math.max(startPct, Math.min(100, Number(this.rangeEnd) || 100))
      if (this.isEmpty) {
        return { mode: 'split', startPct, endPct }
      }
      const startText = this.displayTimeStart
      const endText = this.displayTimeEnd
      const barW = this.barInnerWidth > 0 ? this.barInnerWidth : 400
      const gapPx = ((endPct - startPct) / 100) * barW
      const estCharPx = 5.8
      const minGapPx = startText.length * estCharPx + endText.length * estCharPx + 8
      if (gapPx < minGapPx) {
        return {
          mode: 'combined',
          leftPct: (startPct + endPct) / 2,
          text: `${startText} ~ ${endText}`
        }
      }
      return { mode: 'split', startPct, endPct }
    }
  },
  watch: {
    points: {
      handler() {
        this.renderChart()
      },
      deep: true
    },
    rangeStart() {
      this.renderChart()
    },
    rangeEnd() {
      this.renderChart()
    },
    progressRatio() {
      this.updateProgressMark()
    }
  },
  mounted() {
    this._onResize = () => {
      this.syncBarWidth()
      if (this.chart) this.chart.resize()
    }
    this._onThemeChange = () => this.renderChart()
    window.addEventListener('resize', this._onResize)
    window.addEventListener('layout-theme-change', this._onThemeChange)
    this._resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          // 左侧折叠/展开会改容器宽度但不触发 window.resize，需同步 echarts
          this._onResize()
        })
      : null
    if (this._resizeObserver && this.$refs.wrapEl) {
      this._resizeObserver.observe(this.$refs.wrapEl)
    }
    this.syncBarWidth()
    this.renderChart()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
    window.removeEventListener('layout-theme-change', this._onThemeChange)
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    this.disposeChart()
  },
  methods: {
    syncBarWidth() {
      const el = this.$refs.wrapEl
      this.barInnerWidth = el && el.clientWidth ? el.clientWidth : 0
    },
    disposeChart() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },
    initChart() {
      const el = this.$refs.chartEl
      if (!el) return null
      if (!this.chart) {
        this.chart = echarts.init(el)
        this.chart.on('datazoom', (params) => {
          const batch = params && Array.isArray(params.batch) ? params.batch : null
          const source = batch && batch.length ? batch[0] : params
          if (!source) return
          const start = source.start != null ? source.start : this.rangeStart
          const end = source.end != null ? source.end : this.rangeEnd
          this.$emit('range-change', { start, end })
        })
      }
      return this.chart
    },
    buildProgressMarkLine(ratio, dataLen) {
      if (!dataLen || dataLen < 2) return null
      const idx = Math.min(dataLen - 1, Math.max(0, Math.round(ratio * (dataLen - 1))))
      return {
        silent: true,
        symbol: ['none', 'none'],
        animation: false,
        lineStyle: { color: getLayoutPrimaryColor(), width: 2, type: 'solid' },
        label: { show: false },
        data: [{ xAxis: idx }]
      }
    },
    buildOption(points) {
      const primary = getLayoutPrimaryColor()
      const times = points.map((p) => p.time)
      const speeds = points.map((p) => p.speed)
      const showDataZoom = points.length > 1
      const ratio = Math.min(1, Math.max(0, Number(this.progressRatio) || 0))
      return {
        animation: false,
        color: [primary],
        grid: {
          left: 4,
          right: 4,
          top: 2,
          bottom: showDataZoom ? 26 : 4,
          containLabel: false
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: times,
          show: false
        },
        yAxis: {
          type: 'value',
          show: false,
          min: 0,
          splitLine: { show: false }
        },
        dataZoom: showDataZoom
          ? [
              {
                type: 'slider',
                xAxisIndex: 0,
                start: this.rangeStart,
                end: this.rangeEnd,
                ...buildDataZoomStyle()
              }
            ]
          : [],
        series: [
          {
            name: '轨迹',
            type: 'line',
            smooth: true,
            symbol: 'none',
            lineStyle: { width: 1, color: primary, opacity: 0.65 },
            areaStyle: { color: getLayoutPrimaryRgba(0.15) },
            data: speeds,
            markLine: this.buildProgressMarkLine(ratio, speeds.length)
          }
        ]
      }
    },
    updateProgressMark() {
      if (!this.chart) return
      const points = Array.isArray(this.points) ? this.points : []
      if (!points.length) return
      const ratio = Math.min(1, Math.max(0, Number(this.progressRatio) || 0))
      this.chart.setOption({
        series: [
          {
            markLine: this.buildProgressMarkLine(ratio, points.length)
          }
        ]
      })
    },
    renderChart() {
      this.$nextTick(() => {
        const points = Array.isArray(this.points) ? this.points : []
        if (!points.length) {
          this.disposeChart()
          return
        }
        const chart = this.initChart()
        if (!chart) return
        chart.setOption(this.buildOption(points), true)
        chart.resize()
      })
    }
  }
}
</script>

<style scoped>
.playback-range-bar {
  position: relative;
  width: 100%;
  padding: 0 0.25rem 0.125rem;
  min-height: calc(14px + 1.75rem);
}

.playback-range-bar >>> .el-loading-spinner {
  margin-top: -20px;
}

.playback-range-bar >>> .el-loading-spinner .el-loading-text {
  font-size: 12px;
  color: #606266;
  margin-top: 4px;
}

.playback-range-bar >>> .el-loading-spinner .el-icon-loading {
  font-size: 18px;
  color: var(--layout-primary, #087dff);
}

.playback-range-bar__inner {
  position: relative;
  width: 100%;
  padding-top: 14px;
  min-height: calc(14px + 1.75rem);
}

.playback-range-bar__chart {
  width: 100%;
  height: 1.75rem;
}

.playback-range-bar__empty {
  width: 100%;
  height: 1.75rem;
  background: #f5f7fa;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
}

.playback-range-bar__empty-line {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 2px;
  height: 2px;
  background: var(--layout-primary, #087dff);
  opacity: 0.75;
}

.playback-range-bar__time {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 10px;
  line-height: 1.2;
  color: #606266;
  white-space: nowrap;
  pointer-events: none;
  z-index: 2;
  padding: 0 2px;
}

.playback-range-bar__time--start {
  transform: translateX(0);
}

.playback-range-bar__time--end {
  transform: translateX(-100%);
}

.playback-range-bar__time--combined {
  transform: translateX(-50%);
  max-width: 96%;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.playback-range-bar__time.is-placeholder {
  color: #c0c4cc;
}
</style>
